import React, { useState, useEffect } from 'react';
import '../stylings/BucksContact.css';

// API Endpoints - using both Customer (DB) and Admin (Email) APIs
const CUSTOMER_API_URL = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com';  // Database save (VPC)
const ADMIN_API_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com';     // Email send (No VPC)

function BucksContact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // Track email sending status
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Save contact message to database
  const saveContactMessage = async (formData) => {
    console.log('Step 1: Saving contact message to database...');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for DB save
    
    try {
      const resp = await fetch(`${CUSTOMER_API_URL}/default/bucks-customer-contact-messages-post`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        mode: 'cors',
        body: JSON.stringify(formData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Database save response status:', resp.status);

      let payload;
      try {
        const textResponse = await resp.text();
        console.log('Database save raw response:', textResponse);
        payload = textResponse ? JSON.parse(textResponse) : {};
      } catch (parseError) {
        console.error('Failed to parse database response:', parseError);
        throw new Error(`Failed to parse database response: ${resp.status} ${resp.statusText}`);
      }

      if (!resp.ok) {
        throw new Error(payload.error || `Database save failed with status: ${resp.status}`);
      }

      console.log('✅ Contact message saved to database successfully');
      return { success: true, payload };

    } catch (err) {
      console.error('❌ Database save error:', err);
      throw err;
    }
  };

  // Send email notification
  const sendEmailNotification = async (formData) => {
    console.log('Step 2: Sending email notification...');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout for email
    
    try {
      const resp = await fetch(`${ADMIN_API_URL}/default/bucks-customer-contact-messages-admin-receive-post`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        mode: 'cors',
        body: JSON.stringify(formData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Email send response status:', resp.status);

      let payload;
      try {
        const textResponse = await resp.text();
        console.log('Email send raw response:', textResponse);
        payload = textResponse ? JSON.parse(textResponse) : {};
      } catch (parseError) {
        console.error('Failed to parse email response:', parseError);
        // Don't throw here - email failure shouldn't fail the whole process
        return { success: false, error: 'Failed to parse email response' };
      }

      if (!resp.ok) {
        console.error('❌ Email send failed:', payload.error || `Status: ${resp.status}`);
        return { success: false, error: payload.error || `Email send failed with status: ${resp.status}` };
      }

      console.log('✅ Email notification sent successfully');
      return { success: true, payload };

    } catch (err) {
      console.error('❌ Email send error:', err);
      return { 
        success: false, 
        error: err.name === 'AbortError' ? 'Email send timeout' : err.message 
      };
    }
  };

  // Handle form submission with sequential API calls
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);
    setEmailStatus(null);

    try {
      console.log('🚀 Starting contact form submission...');
      console.log('Form data:', formData);

      // Step 1: Save to database first (this is critical)
      console.log('📝 Step 1: Saving contact message...');
      const dbResult = await saveContactMessage(formData);
      
      if (!dbResult.success) {
        throw new Error('Failed to save contact message to database');
      }

      // Step 2: Send email notification (this is nice-to-have)
      console.log('📧 Step 2: Sending email notification...');
      setEmailStatus('sending');
      const emailResult = await sendEmailNotification(formData);
      
      if (emailResult.success) {
        setEmailStatus('sent');
        console.log('🎉 Both database save and email notification completed successfully');
      } else {
        setEmailStatus('failed');
        console.warn('⚠️ Database save successful, but email notification failed:', emailResult.error);
        // Don't throw error here - the form submission was still successful
      }

      // Show success message regardless of email status
      setFormSubmitted(true);
      console.log('✅ Contact form submission completed');

      // Reset form after 6 seconds
      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setEmailStatus(null);
      }, 6000);

    } catch (err) {
      console.error('💥 Contact form submission error:', err);
      
      let errorMessage = 'Sorry, something went wrong. Please try again later.';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again or contact us directly at (708) 359-4592.';
      } else if (err.message) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again, or contact us directly at (708) 359-4592.';
        } else if (err.message.includes('400')) {
          errorMessage = 'Please check that all required fields are filled out correctly and try again.';
        } else if (err.message.includes('500')) {
          errorMessage = 'Our system is experiencing issues. Please try again or contact us directly at (708) 359-4592.';
        } else {
          errorMessage = `${errorMessage} (${err.message})`;
        }
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get email status message for UI
  const getEmailStatusMessage = () => {
    switch (emailStatus) {
      case 'sending':
        return '📧 Sending email notification...';
      case 'sent':
        return '✅ Email notification sent to our team';
      case 'failed':
        return '⚠️ Message saved, but email notification failed';
      default:
        return '📧 Email notification sent to our team';
    }
  };

  return (
    <div className="bucks-contact">
      {/* Contact Hero Section */}
      <section className={`contact-hero ${isVisible ? 'visible' : ''}`}>
        <div className="contact-hero-content">
          <h1>Get In Touch</h1>
          <p className="contact-hero-subtitle">
            We'd love to hear from you! Reach out with questions, special orders, or just to say hello.
          </p>
        </div>
      </section>

      {/* Contact Main Content */}
      <section className="contact-main">
        <div className="contact-container">
          {/* Contact Information Column */}
          <div className="contact-info">
            <div className="info-card">
              <h2>Contact Details</h2>

              <div className="info-group">
                <div className="info-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </div>
                <div className="info-content">
                  <h3>Visit Our Farmstand</h3>
                  <address>
                    4250 W 123rd St<br />
                    Alsip, IL 60803
                  </address>
                </div>
              </div>

              <div className="info-group">
                <div className="info-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className="info-content">
                  <h3>Business Hours</h3>
                  <div className="hours-list">
                    <div className="hours-item">
                      <span className="day">Monday - Friday</span>
                      <span className="time">9AM – 4:00PM</span>
                    </div>
                    <div className="hours-item">
                      <span className="day">Saturday</span>
                      <span className="time">9AM – 1PM</span>
                    </div>
                    <div className="hours-item">
                      <span className="day">Sunday</span>
                      <span className="time">Closed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-group">
                <div className="info-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="info-content">
                  <h3>Phone</h3>
                  <p><a href="tel:+17083594592">(708) 359-4592</a></p>
                </div>
              </div>

              <div className="info-group">
                <div className="info-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="info-content">
                  <h3>Email</h3>
                  <p><a href="mailto:bucksfarmstand@gmail.com">bucksfarmstand@gmail.com</a></p>
                </div>
              </div>

              <div className="social-links">
                <h3>Connect With Us</h3>
                <div className="social-icons">
                  <a href="https://facebook.com" aria-label="Buck's Farmstand on Facebook" className="social-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </a>
                  <a href="https://instagram.com" aria-label="Buck's Farmstand on Instagram" className="social-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                  <a href="https://twitter.com" aria-label="Buck's Farmstand on Twitter" className="social-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="map-container">
              <iframe
                title="Buck's Farmstand Location Map"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
                src="https://maps.google.com/maps?q=4250+W+123rd+St,+Alsip,+IL+60803&z=15&output=embed"
              ></iframe>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="contact-form-container">
            <div className="form-card">
              <h2>Send Us a Message</h2>
              <p className="form-intro">
                Have a question about our products or services? Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {formSubmitted ? (
                <div className="form-success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. Your message has been received and we'll be in touch with you shortly.</p>
                  {emailStatus && (
                    <p style={{ 
                      fontSize: '0.9rem', 
                      color: emailStatus === 'failed' ? '#dc2626' : '#6b5a44', 
                      marginTop: '1rem' 
                    }}>
                      {getEmailStatusMessage()}
                    </p>
                  )}
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  {submitError && (
                    <div className="form-error">
                      {submitError}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      required
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone (Optional)</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      required
                      placeholder="What can we help you with?"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      required
                      placeholder="Tell us more about your question or request..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className={`submit-btn ${isSubmitting ? 'loading' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="submit-loader"></div>
                        <span>
                          {emailStatus === 'sending' ? 'Sending Email...' : 'Sending Message...'}
                        </span>
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>

                  <p style={{ fontSize: '0.85rem', color: '#8a7a64', textAlign: 'center', marginTop: '1rem' }}>
                    * Required fields. We typically respond within 24 hours.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Do you offer delivery services?</h3>
              <p>Yes! We offer local delivery within a 10-mile radius of our farmstand. Orders must be placed by 2PM for next-day delivery.</p>
            </div>

            <div className="faq-item">
              <h3>How can I place a special order?</h3>
              <p>Special orders can be placed by calling our farmstand directly or by filling out the contact form above with your request details.</p>
            </div>

            <div className="faq-item">
              <h3>Are your products organic?</h3>
              <p>Most of our produce is grown using organic practices, though not all are certified organic. Feel free to ask about specific items!</p>
            </div>

            <div className="faq-item">
              <h3>Do you offer Buck's Box subscriptions year-round?</h3>
              <p>Yes, our Buck's Box subscription is available throughout the year, with seasonal variations based on what's fresh and available.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BucksContact;